/* global console */

import { withCredentials } from "../../apiClient";
import { buildApiUrl } from "../../config/api";
import { checkAuthStatus } from "../../utils/authStatus";
import { fetchWithRetry } from "../../utils/http";

export interface LogoCandidate {
  logoCandidateRef: string;
  matchedCompanyName?: string;
  matchedTicker?: string;
  logoUrl?: string;
  outOfDate?: boolean;
}

export interface LogoResult {
  success: boolean;
  companyName?: string;
  logoUrl?: string;
  candidates?: LogoCandidate[];
  source?: string;
  confidenceScore?: number;
  matchedCompanyName?: string;
  matchedTicker?: string;
  outOfDate?: boolean;
  needsReview?: boolean;
  requestId?: string;
  error?: string;
  message?: string;
  debugLog?: string[];
}

export interface LogoBatchResult {
  success: boolean;
  results: LogoResult[];
  resolvedCount: number;
  failedCount: number;
  failedCompanies: Array<{ companyName?: string; error?: string; message?: string }>;
  error?: string;
  debugLog?: string[];
}

function normalizeLogoUrl(rawLogoUrl?: string): string | undefined {
  if (!rawLogoUrl) return undefined;
  return /^https?:\/\//i.test(rawLogoUrl)
    ? rawLogoUrl
    : buildApiUrl(rawLogoUrl.startsWith("/") ? rawLogoUrl : `/${rawLogoUrl}`);
}

function normalizeCompanyKey(companyName?: string): string {
  return String(companyName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function logoResultRank(result: LogoResult): number {
  if (
    !result.success ||
    (!result.logoUrl && !result.candidates?.some((candidate) => candidate.logoUrl))
  ) {
    return Number.NEGATIVE_INFINITY;
  }
  return typeof result.confidenceScore === "number" && Number.isFinite(result.confidenceScore)
    ? result.confidenceScore
    : 0;
}

function shouldReplaceLogoResult(current: LogoResult, candidate: LogoResult): boolean {
  if (candidate.success !== current.success) return candidate.success === true;
  const currentRank = logoResultRank(current);
  const candidateRank = logoResultRank(candidate);
  if (candidateRank !== currentRank) return candidateRank > currentRank;
  if (candidate.outOfDate !== current.outOfDate) return current.outOfDate === true;
  return false;
}

function capLogoResultsByCompany(results: LogoResult[]): LogoResult[] {
  const byCompany = new Map<string, LogoResult>();
  const orderedKeys: string[] = [];

  results.forEach((result, index) => {
    const key = normalizeCompanyKey(result.companyName) || `__index_${index}`;
    const existing = byCompany.get(key);
    if (!existing) {
      byCompany.set(key, result);
      orderedKeys.push(key);
      return;
    }
    if (shouldReplaceLogoResult(existing, result)) {
      byCompany.set(key, result);
    }
  });

  return orderedKeys.map((key) => byCompany.get(key)!);
}

export async function getCompanyLogos(
  companyNames: string[],
  size?: number,
): Promise<LogoBatchResult> {
  const debugLog: string[] = [];
  const log = (msg: string) => {
    debugLog.push(msg);
    console.log(`[LOGO] ${msg}`);
  };

  if (!Array.isArray(companyNames) || companyNames.length === 0) {
    return {
      success: false,
      results: [],
      resolvedCount: 0,
      failedCount: 0,
      failedCompanies: [],
      error: "No company names provided",
      debugLog,
    };
  }

  const logoSize = size && size > 0 ? size : 128;
  log(`Resolving ${companyNames.length} compan${companyNames.length === 1 ? "y" : "ies"}`);

  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/logo/resolve"),
      withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyNames, size: logoSize }),
      }),
      1,
      "LOGO",
    );
    checkAuthStatus(response);

    type Payload = {
      success?: boolean;
      results?: LogoResult[];
      resolvedCount?: number;
      failedCount?: number;
      failedCompanies?: Array<{ companyName?: string; error?: string; message?: string }>;
      error?: string;
      message?: string;
      debugLog?: string[];
    };

    let payload: Payload | null = null;
    try {
      payload = (await response.clone().json()) as Payload;
    } catch {
      payload = null;
    }

    if (payload?.debugLog) {
      payload.debugLog.forEach(log);
    }

    if (!response.ok || !Array.isArray(payload?.results)) {
      const message = payload?.message || payload?.error || `HTTP ${response.status}`;
      log(`Failed: ${message}`);
      return {
        success: false,
        results: [],
        resolvedCount: 0,
        failedCount: 0,
        failedCompanies: [],
        error: message,
        debugLog,
      };
    }

    const results = capLogoResultsByCompany(
      payload.results.map((result) => {
        const normalizedLogoUrl = normalizeLogoUrl(result.logoUrl);
        const candidates = Array.isArray(result.candidates)
          ? result.candidates.map((candidate) => ({
              ...candidate,
              logoUrl: normalizeLogoUrl(candidate.logoUrl),
            }))
          : undefined;
        const hasCandidateLogo = candidates?.some((candidate) => candidate.logoUrl) === true;
        // Leave result.debugLog on the result; batch debugLog is logged separately.
        if (result.success && (normalizedLogoUrl || hasCandidateLogo)) {
          log(
            `Resolved "${result.companyName || "unknown"}" (${result.source || "unknown"})`
          );
        } else if (!result.success) {
          log(
            `Failed "${result.companyName || "unknown"}": ${
              result.message || result.error || "unknown error"
            }`
          );
        }
        return {
          ...result,
          logoUrl: normalizedLogoUrl,
          ...(candidates ? { candidates } : {}),
        };
      })
    );
    const failedCompanies =
      results
        .filter((result) => !result.success)
        .map((result) => ({
          companyName: result.companyName,
          error: result.error,
          message: result.message,
        }));
    const resolvedCount = results.filter(
      (result) =>
        result.success &&
        Boolean(result.logoUrl || result.candidates?.some((candidate) => candidate.logoUrl))
    ).length;
    const failedCount = failedCompanies.length;
    const success = resolvedCount > 0;
    if (!success) {
      const message =
        payload.message ||
        payload.error ||
        failedCompanies[0]?.message ||
        failedCompanies[0]?.error ||
        "No company logos were found.";
      log(`Failed: ${message}`);
      return {
        success: false,
        results,
        resolvedCount: 0,
        failedCount,
        failedCompanies,
        error: message,
        debugLog,
      };
    }
    return {
      success: true,
      results,
      resolvedCount,
      failedCount,
      failedCompanies,
      debugLog,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    log(`Error: ${msg}`);
    return {
      success: false,
      results: [],
      resolvedCount: 0,
      failedCount: 0,
      failedCompanies: [],
      error: msg,
      debugLog,
    };
  }
}

export async function getCompanyLogo(companyName: string, size?: number): Promise<LogoResult> {
  if (!companyName) {
    return { success: false, error: "No company name provided", debugLog: [] };
  }

  const batch = await getCompanyLogos([companyName], size);
  if (!batch.success) {
    return { success: false, error: batch.error, debugLog: batch.debugLog };
  }
  const result =
    batch.results[0] ?? {
      success: false,
      companyName,
      error: "No company logo result returned",
      debugLog: batch.debugLog,
    };
  if (result.logoUrl) return result;

  // Excel's legacy single-logo callers still require one URL. PowerPoint uses
  // getCompanyLogos and selects an explicit opaque candidate ref instead.
  const firstCandidate = result.candidates?.find((candidate) => candidate.logoUrl);
  return firstCandidate
    ? {
        ...result,
        logoUrl: firstCandidate.logoUrl,
        matchedCompanyName: firstCandidate.matchedCompanyName,
        matchedTicker: firstCandidate.matchedTicker,
        outOfDate: firstCandidate.outOfDate,
      }
    : result;
}
